import React, { useEffect, useState, useRef, useCallback } from 'react';

// Completely isolated component that doesn't sync with parent during typing
const ChangeReasonPrompt = React.memo(
  ({ subQuestionId, isVisible, reason, onReasonChange, onSubmit, onSkip }) => {
    const textareaRef = useRef(null);
    const [localValue, setLocalValue] = useState(reason || '');
    const mountedRef = useRef(false);

    // Only update local value when first mounting or when subQuestionId changes
    useEffect(() => {
      if (!mountedRef.current || isVisible) {
        setLocalValue(reason || '');
        mountedRef.current = true;
      }
    }, [subQuestionId, isVisible, reason]);

    // Completely isolated handlers that don't call parent until submit
    const handleChange = useCallback(e => {
      setLocalValue(e.target.value);
    }, []);

      const handleSubmit = useCallback(() => {
    // Pass the reasoning value directly to onSubmit instead of relying on parent state
    onSubmit(subQuestionId, localValue);
  }, [onSubmit, subQuestionId, localValue]);

    const handleSkip = useCallback(() => {
      onSkip(subQuestionId);
    }, [onSkip, subQuestionId]);

    if (!isVisible) {
      return null;
    }

    return (
      <div
        className="mb-3 rounded-xl border overflow-hidden transition-all duration-300 animate-scale-entrance"
        style={{
          background: 'linear-gradient(135deg, #fdf8f3 0%, #f9ede2 100%)',
          border: '1px solid rgba(228, 177, 122, 0.12)',
          boxShadow:
            'rgba(228, 177, 122, 0.06) 0px 2px 4px -2px, rgba(228, 177, 122, 0.04) 0px 4px 8px -2px',
          backdropFilter: 'blur(12px) saturate(180%)',
          WebkitBackdropFilter: 'blur(12px) saturate(180%)'
        }}
      >
        <div
          className="p-4 border-b"
          style={{
            background:
              'linear-gradient(to right, rgba(228, 177, 122, 0.08), rgba(228, 177, 122, 0.04))',
            borderBottomColor: 'rgba(228, 177, 122, 0.1)'
          }}
        >
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center w-8 h-8 rounded-full"
              style={{
                background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                color: 'white',
                boxShadow: '0 2px 8px rgba(228, 177, 122, 0.2)'
              }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h4
              className="text-lg font-semibold m-0"
              style={{
                color: '#8c6b4a',
                letterSpacing: '-0.01em'
              }}
            >
              Why did you change the grade?
            </h4>
          </div>
        </div>

        <div className="p-4">
          <textarea
            ref={textareaRef}
            value={localValue}
            placeholder="e.g., Found additional correct work, Misunderstood the rubric initially, Student showed more understanding than initially assessed..."
            className="w-full p-3 mb-4 rounded-lg resize-none transition-all duration-300 focus:outline-none"
            style={{
              background: 'white',
              border: '1px solid rgba(228, 177, 122, 0.2)',
              color: '#8c6b4a',
              fontSize: '14px',
              lineHeight: '1.6',
              boxShadow: 'inset 0 1px 2px rgba(228, 177, 122, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            rows="3"
            autoFocus
            onChange={handleChange}
          />

          <div className="flex justify-end gap-3">
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 hover:transform hover:-translate-y-1"
              style={{
                background: 'white',
                color: '#a17c57',
                border: '1px solid rgba(228, 177, 122, 0.2)',
                boxShadow: '0 1px 2px rgba(228, 177, 122, 0.05)'
              }}
              onClick={handleSkip}
            >
              Skip
            </button>
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium text-white transition-all duration-300 hover:transform hover:-translate-y-1"
              style={{
                background: 'linear-gradient(135deg, #e4b17a, #c99b69)',
                boxShadow: '0 4px 12px rgba(228, 177, 122, 0.2)',
                border: 'none'
              }}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default ChangeReasonPrompt;